<template>
  <div class="avatar-filepond-wrapper" :class="`shape-${shape}`">
    <!-- Native file input - NOT wrapped in label to prevent auto-trigger -->
    <input
      ref="nativeFileInput"
      type="file"
      :accept="acceptedFileTypes.join(',')"
      class="native-file-input"
      @change="handleNativeFileChange"
    />

    <!-- Custom Avatar Preview/Trigger -->
    <div
      class="avatar-trigger"
      :class="{ 'has-image': currentDisplayUrl, 'is-uploading': isUploading }"
      @click="handleClick"
    >
      <!-- Preview Image -->
      <img
        v-if="currentDisplayUrl && !isUploading"
        :src="currentDisplayUrl"
        alt="Avatar"
        class="avatar-preview"
      />

      <!-- Placeholder -->
      <div v-else-if="!isUploading" class="avatar-placeholder">
        <slot name="placeholder">
          <f7-icon f7="camera_fill" size="32" color="gray"></f7-icon>
          <span class="avatar-label">{{ placeholderText }}</span>
        </slot>
      </div>

      <!-- Upload Progress -->
      <div v-if="isUploading" class="upload-progress">
        <f7-progressbar :progress="uploadProgress" color="primary"></f7-progressbar>
        <span class="progress-text">{{ Math.round(uploadProgress) }}%</span>
      </div>
    </div>

    <!-- Hidden FilePond Instance -->
    <file-pond
      ref="pond"
      name="file"
      :label-idle="labelIdle"
      :allow-multiple="false"
      :server="serverConfig"
      :files="fileItems"
      :accepted-file-types="acceptedFileTypes"
      :max-file-size="maxFileSize"
      :allow-image-preview="false"
      style-panel-layout="compact"
      credits="false"
      class="filepond-hidden"
      @init="handleFilePondInit"
      @addfile="handleAddFile"
      @processfile="handleProcessFile"
      @processfileprogress="handleProgress"
      @removefile="handleRemoveFile"
      @error="handleError"
    ></file-pond>
  </div>
</template>

<script setup>
import 'filepond/dist/filepond.min.css';
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size';
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import { ref, computed, watch, toRef, onBeforeUnmount } from 'vue';
import vueFilePond from 'vue-filepond';
import { apiClient } from '@/httpConfig/config/axios';

const props = defineProps({
  modelValue: { type: String, default: '' },
  serverUrl: { type: String, default: '/filepond' },
  acceptedFileTypes: { type: Array, default: () => ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'] },
  maxFileSize: { type: String, default: '5MB' },
  placeholderText: { type: String, default: 'رفع صورة' },
  labelIdle: { type: String, default: 'اسحب وأفلت أو <span class="filepond--label-action">تصفح</span>' },
  shape: { type: String, default: 'avatar', validator: (v) => ['avatar', 'receipt'].includes(v) },
});

const emit = defineEmits(['update:modelValue', 'upload-success', 'upload-error', 'remove', 'click']);

const FilePond = vueFilePond(FilePondPluginFileValidateType, FilePondPluginFileValidateSize);

const pond = ref(null);
const nativeFileInput = ref(null);
const fileItems = ref([]);
const isUploading = ref(false);
const uploadProgress = ref(0);
const currentServerId = ref('');
const uploadedFileUrl = ref('');
const modelValueRef = toRef(props, 'modelValue');

const currentDisplayUrl = computed(() => uploadedFileUrl.value || modelValueRef.value);

const fileToDataURL = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = (e) => reject(e);
    reader.readAsDataURL(file);
  });
};

const serverConfig = computed(() => ({
  process: async (fieldName, file, metadata, load, error, progress, abort) => {
    try {
      const formData = new FormData();
      formData.append('file', file, file.name);
      if (metadata) {
        Object.keys(metadata).forEach(key => formData.append(key, metadata[key]));
      }
      const controller = new AbortController();
      const response = await apiClient.post(props.serverUrl, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        signal: controller.signal,
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            progress(progressEvent.lengthComputable, progressEvent.loaded, progressEvent.total);
          }
        },
      });
      const serverId = typeof response.data === 'string' ? response.data : (response.data?.id || response.data?.path || response.data);
      if (!serverId) throw new Error('Invalid server response: no file ID received');
      load(serverId);
      return { abort: () => { controller.abort(); abort(); } };
    } catch (err) {
      if (err.name === 'CanceledError' || err.name === 'AbortError') {
        abort();
      } else {
        let errorMessage = 'فشل رفع الملف';
        if (err.response?.status === 422) {
          const validationErrors = err.response?.data?.errors || err.response?.data;
          if (validationErrors && typeof validationErrors === 'object') {
            const [firstError] = Object.values(validationErrors);
            errorMessage = Array.isArray(firstError) ? firstError[0] : firstError;
          } else {
            errorMessage = 'الملف غير صالح';
          }
        } else if (err.response?.data?.message) {
          errorMessage = err.response.data.message;
        } else if (err.message) {
          errorMessage = err.message;
        }
        error(errorMessage);
      }
    }
  },
  revert: async (uniqueFileId, load, error) => {
    try {
      await apiClient.delete(props.serverUrl, { data: uniqueFileId, headers: { 'Content-Type': 'text/plain' } });
      load();
    } catch (err) {
      error(err.response?.data?.message || err.message || 'فشل حذف الملف');
    }
  },
  restore: null,
  load: null,
  fetch: null,
}));

const handleClick = () => {
  if (!isUploading.value) {
    emit('click');
  }
};

const triggerFilePond = () => {
  if (!isUploading.value && nativeFileInput.value) {
    nativeFileInput.value.click();
  }
};

const handleNativeFileChange = (event) => {
  const file = event.target.files?.[0];
  if (file && pond.value) {
    pond.value.addFile(file);
    event.target.value = '';
  }
};

const handleFilePondInit = () => {};

const handleAddFile = (error, _file) => {
  if (error) {
    emit('upload-error', error);
    return;
  }
  isUploading.value = true;
  uploadProgress.value = 0;
};

const handleProgress = (file, progress) => {
  uploadProgress.value = progress * 100;
};

const handleProcessFile = async (error, file) => {
  if (error) {
    isUploading.value = false;
    uploadProgress.value = 0;
    emit('upload-error', error);
    return;
  }
  isUploading.value = false;
  uploadProgress.value = 100;
  const { serverId } = file;
  if (file.file) {
    try {
      uploadedFileUrl.value = await fileToDataURL(file.file);
    } catch {
      uploadedFileUrl.value = '';
    }
  }
  currentServerId.value = serverId;
  emit('update:modelValue', serverId);
  emit('upload-success', { serverId, file: file.file, displayUrl: uploadedFileUrl.value });
};

const handleRemoveFile = (error, _file) => {
  if (error) {
    emit('upload-error', error);
    return;
  }
  uploadedFileUrl.value = '';
  uploadProgress.value = 0;
  currentServerId.value = '';
  emit('update:modelValue', '');
  emit('remove');
};

const handleError = (error) => {
  isUploading.value = false;
  uploadProgress.value = 0;
  emit('upload-error', error);
};

const clear = () => {
  if (pond.value) pond.value.removeFiles();
  uploadedFileUrl.value = '';
  currentServerId.value = '';
  isUploading.value = false;
  uploadProgress.value = 0;
  if (nativeFileInput.value) nativeFileInput.value.value = '';
};

watch(modelValueRef, (newValue) => {
  if (newValue === currentServerId.value) return;
  if (!newValue) {
    uploadedFileUrl.value = '';
    if (pond.value?.getFiles().length > 0) clear();
  } else {
    uploadedFileUrl.value = '';
    currentServerId.value = newValue;
  }
});

onBeforeUnmount(() => {
  uploadedFileUrl.value = '';
});

defineExpose({ clear, triggerFilePond, nativeFileInput });
</script>

<style scoped>
.avatar-filepond-wrapper {
    position: relative;
    display: inline-block;
}

.avatar-filepond-wrapper.shape-receipt {
    display: block;
    width: 100%;
}

.native-file-input {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
    pointer-events: none;
}

.avatar-trigger {
    width: 90px;
    height: 90px;
    border-radius: 12px;
    overflow: hidden;
    border: 2px dashed var(--f7-list-item-border-color);
    background: var(--f7-page-bg-color);
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    position: relative;
}

.avatar-trigger:active {
    transform: scale(0.95);
    border-color: var(--f7-theme-color);
    background: rgba(var(--f7-theme-color-rgb), 0.05);
}

.shape-receipt .avatar-trigger {
    width: 100%;
    height: 180px;
    border-radius: 14px;
}

.shape-receipt .avatar-trigger:active {
    transform: none;
}

.avatar-trigger.is-uploading {
    cursor: wait;
    border-color: var(--f7-theme-color);
    background: rgba(var(--f7-theme-color-rgb), 0.05);
    pointer-events: none;
}

.avatar-preview {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.shape-receipt .avatar-preview {
    object-fit: contain;
}

.avatar-placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 8px;
    text-align: center;
}

.avatar-label {
    font-size: 11px;
    line-height: 1.2;
    color: var(--f7-text-color);
    font-weight: 500;
}

.upload-progress {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: rgba(var(--f7-theme-color-rgb), 0.1);
    backdrop-filter: blur(4px);
    gap: 8px;
    padding: 12px;
}

.progress-text {
    font-size: 13px;
    font-weight: 600;
    color: var(--f7-theme-color);
}

.filepond-hidden {
    display: none !important;
}

@media (max-width: 380px) {
    .avatar-trigger {
        width: 80px;
        height: 80px;
    }
    .avatar-label {
        font-size: 10px;
    }
}
</style>
